import { useState, useEffect } from 'react';
import api from '../../services/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  ArrowLeft, User, Wallet, Gamepad2, History, Shield, DollarSign,
  Ban, CheckCircle, Activity, Users, Bell, Copy, RefreshCw, Plus,
  Minus, ArrowDownCircle, ArrowUpCircle, Clock, Star, Eye, BarChart3,
  KeyRound, Link2
} from 'lucide-react';

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export default function PlayerDetailPage({ playerId, onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showAdjust, setShowAdjust] = useState(false);
  const [adjustAmount, setAdjustAmount] = useState('');
  const [adjustType, setAdjustType] = useState('add');
  const [adjustReason, setAdjustReason] = useState('');
  const [adjusting, setAdjusting] = useState(false);
  const [showReset, setShowReset] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [resetting, setResetting] = useState(false);

  // Link-provider-account panel state.
  const [showLink, setShowLink] = useState(false);
  const [availableGames, setAvailableGames] = useState([]);
  const [linkGameId, setLinkGameId] = useState('');
  const [linkUsername, setLinkUsername] = useState('');
  const [linkPassword, setLinkPassword] = useState('');
  const [linkNote, setLinkNote] = useState('');
  const [linking, setLinking] = useState(false);
  const [checkingBalances, setCheckingBalances] = useState({});

  useEffect(() => { fetchProfile(); }, [playerId]);

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/api/admin/player-profile/${playerId}`);
      setData(res.data);
    } catch { toast.error('Failed to load player profile'); }
    finally { setLoading(false); }
  };

  const handleAdjust = async () => {
    if (!adjustAmount || !adjustReason) { toast.error('Amount and reason required'); return; }
    setAdjusting(true);
    try {
      const amt = parseFloat(adjustAmount) * (adjustType === 'subtract' ? -1 : 1);
      await api.post(`/api/admin/users/${playerId}/adjust-wallet`, { amount: amt, adjust_type: 'balance', reason: adjustReason });
      toast.success(`Wallet ${adjustType === 'add' ? 'credited' : 'debited'}`);
      setShowAdjust(false); setAdjustAmount(''); setAdjustReason('');
      fetchProfile();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setAdjusting(false); }
  };

  const toggleDisable = async () => {
    try {
      await api.post(`/api/admin/users/${playerId}/status`, { disabled: !data.player.disabled });
      toast.success(data.player.disabled ? 'Player enabled' : 'Player disabled');
      fetchProfile();
    } catch { toast.error('Failed'); }
  };

  const forceLogout = async () => {
    try { await api.post(`/api/admin/users/${playerId}/force-logout`); toast.success('Sessions invalidated'); } catch { toast.error('Failed'); }
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setResetting(true);
    try {
      await api.post(`/api/admin/users/${playerId}/reset-password`, { new_password: newPassword });
      toast.success('Password updated — player is signed out everywhere');
      setShowReset(false);
      setNewPassword('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset password');
    } finally {
      setResetting(false);
    }
  };

  const generateTempPassword = () => {
    // Random 10-char password, mixed-case + digits — easy to read aloud to a
    // locked-out player during a support call.
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
    let out = '';
    for (let i = 0; i < 10; i++) out += chars[Math.floor(Math.random() * chars.length)];
    setNewPassword(out);
  };

  // ── Link provider account (attach an EXISTING provider username to
  // this player so it appears on their web account). ────────────────
  const openLink = async () => {
    setShowLink(v => !v);
    if (availableGames.length === 0) {
      try {
        const res = await api.get('/api/admin/games?limit=200');
        const items = res.data?.data?.items || res.data?.items || res.data?.games || [];
        // Only games with a provider linkage are eligible.
        setAvailableGames(items.filter(g => g.provider_code || g.provider));
      } catch {
        toast.error('Failed to load providers list');
      }
    }
  };

  const handleLinkProvider = async () => {
    if (!linkGameId) { toast.error('Pick a provider'); return; }
    if (!linkUsername.trim() || !linkPassword.trim()) {
      toast.error("Enter the player's existing provider username and password");
      return;
    }
    setLinking(true);
    try {
      const res = await api.post(`/api/admin/users/${playerId}/link-provider-account`, {
        game_id: linkGameId,
        provider_username: linkUsername.trim(),
        provider_password: linkPassword.trim(),
        note: linkNote.trim() || undefined,
      });
      const bal = res.data?.verified_balance ?? 0;
      toast.success(`Linked ${linkUsername.trim()} — verified balance $${Number(bal).toFixed(2)}`);
      setShowLink(false);
      setLinkGameId(''); setLinkUsername(''); setLinkPassword(''); setLinkNote('');
      fetchProfile();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.response?.data?.message || 'Link failed');
    } finally {
      setLinking(false);
    }
  };

  const handleAdminCheckBalance = async (acc) => {
    if (!acc?.id) return;
    setCheckingBalances(prev => ({ ...prev, [acc.id]: true }));
    try {
      const res = await api.post(`/api/admin/player-profile/${playerId}/game-accounts/${acc.id}/check-balance`);
      const d = res.data || {};
      setData(prev => ({
        ...prev,
        game_accounts: (prev.game_accounts || []).map(a => (
          a.id === acc.id
            ? {
                ...a,
                last_known_balance: d.balance_usd,
                last_balance_synced_at: d.last_synced_at,
                last_synced_at: d.last_synced_at,
                provider_status: d.provider_status,
                is_cached: d.is_cached,
              }
            : a
        )),
      }));
      if (d.is_cached) {
        toast.warning(d.error || 'Provider balance unavailable; showing cached balance.');
      } else {
        toast.success(`Balance checked: $${Number(d.balance_usd || 0).toFixed(2)}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || err.response?.data?.message || 'Balance check failed');
    } finally {
      setCheckingBalances(prev => ({ ...prev, [acc.id]: false }));
    }
  };

  const copyText = (t) => { navigator.clipboard.writeText(t); toast.success('Copied!'); };
  const fmtMoney = (v) => `$${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
  const fmtNumber = (v) => Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });

  if (loading) return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;
  if (!data?.player) return <div className="text-center py-12 text-gray-500">Player not found</div>;

  const p = data.player;
  const w = data.wallet || {};
  const tabs = ['overview', 'games', 'transactions', 'activity', 'referrals'];

  return (
    <div className="space-y-4" data-testid="player-detail-page">
      <button onClick={onBack} className="flex items-center gap-2 text-gray-400 hover:text-white text-sm" data-testid="back-to-players">
        <ArrowLeft className="w-4 h-4" /> Back to Players
      </button>

      {/* Player Header */}
      <div className="bg-[#131822] rounded-2xl border border-white/10 p-5 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-full bg-[#D4AF37]/15 flex items-center justify-center text-[#D4AF37] text-xl font-bold">
            {p.username?.charAt(0).toUpperCase() || 'U'}
          </div>
          <div>
            <h2 className="text-xl font-bold text-white">{p.username}</h2>
            <div className="flex items-center gap-1 mt-0.5">
              <p className="text-sm text-gray-400">{p.email}</p>
              <button onClick={() => copyText(p.email)} className="text-gray-600 hover:text-gray-400"><Copy className="w-3 h-3" /></button>
            </div>
            <div className="flex items-center gap-2 mt-1.5">
              <span className={`text-[9px] px-2 py-0.5 rounded-full font-semibold ${p.disabled ? 'bg-red-500/20 text-red-400' : 'bg-green-500/20 text-green-400'}`}>
                {p.disabled ? 'DISABLED' : 'ACTIVE'}
              </span>
              <span className="text-[9px] px-2 py-0.5 rounded-full bg-[#D4AF37]/15 text-[#D4AF37] font-semibold capitalize">{p.achievement_tier || 'Bronze'}</span>
              {p.tenant_id && <span className="text-[9px] px-2 py-0.5 rounded-full bg-blue-500/15 text-blue-400 font-medium">Tenant</span>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={openLink} className="border-white/10 text-xs" data-testid="link-provider-btn">
            <Link2 className="w-3 h-3 mr-1" /> Add Provider Account
          </Button>
          <Button size="sm" variant="outline" onClick={() => setShowReset(v => !v)} className="border-white/10 text-xs" data-testid="reset-password-btn">
            <KeyRound className="w-3 h-3 mr-1" /> Reset Password
          </Button>
          <Button size="sm" variant="outline" onClick={forceLogout} className="border-white/10 text-xs"><Shield className="w-3 h-3 mr-1" /> Force Logout</Button>
          <Button size="sm" onClick={toggleDisable} className={`text-xs ${p.disabled ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}`}>
            {p.disabled ? <><CheckCircle className="w-3 h-3 mr-1" /> Enable</> : <><Ban className="w-3 h-3 mr-1" /> Disable</>}
          </Button>
        </div>
      </div>

      {showLink && (
        <div className="bg-[#131822] rounded-xl border border-[#D4AF37]/40 p-4" data-testid="link-provider-panel">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-8 h-8 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center shrink-0">
              <Link2 className="w-4 h-4 text-[#D4AF37]" />
            </div>
            <div className="text-xs text-gray-400 leading-relaxed flex-1">
              Link an <b className="text-white">existing provider account</b> to this player — their Juwa / Firekirin / CashFrenzy username & password from before they joined the web. We'll verify the account on the provider before saving. Once linked, the player will see that account; provider balance is refreshed only when Check Balance is clicked.
            </div>
          </div>

          <div className="space-y-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold block mb-1">Provider</label>
              <select
                value={linkGameId}
                onChange={e => setLinkGameId(e.target.value)}
                className="w-full bg-[#0B0E14] border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
                data-testid="link-provider-game-select"
              >
                <option value="">— Pick a provider —</option>
                {availableGames.map(g => (
                  <option key={g.id || g.code} value={g.id || g.code}>
                    {g.name || g.code} ({g.provider_code || g.provider || 'provider'})
                  </option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold block mb-1">Provider Username</label>
                <Input
                  value={linkUsername}
                  onChange={e => setLinkUsername(e.target.value)}
                  placeholder="e.g. morpheus707"
                  className="bg-[#0B0E14] border-white/10 font-mono text-sm"
                  autoComplete="off"
                  data-testid="link-provider-username"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold block mb-1">Provider Password</label>
                <Input
                  type="text"
                  value={linkPassword}
                  onChange={e => setLinkPassword(e.target.value)}
                  placeholder="Existing password"
                  className="bg-[#0B0E14] border-white/10 font-mono text-sm"
                  autoComplete="off"
                  data-testid="link-provider-password"
                />
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold block mb-1">Note <span className="text-gray-600 normal-case">(optional — shown in staff activity log)</span></label>
              <Input
                value={linkNote}
                onChange={e => setLinkNote(e.target.value)}
                placeholder="e.g. existing Telegram account, player request 2026-01-12"
                className="bg-[#0B0E14] border-white/10 text-sm"
                data-testid="link-provider-note"
              />
            </div>
            <div className="flex items-center justify-end gap-2 pt-1">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowLink(false)}
                className="border-white/10 text-xs"
                data-testid="link-provider-cancel"
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleLinkProvider}
                disabled={linking || !linkGameId || !linkUsername.trim() || !linkPassword.trim()}
                className="bg-[#D4AF37] text-black text-xs"
                data-testid="link-provider-submit"
              >
                {linking ? 'Verifying with provider…' : 'Verify & Link'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {showReset && (
        <div className="bg-[#131822] rounded-xl border border-white/10 p-4" data-testid="reset-password-panel">
          <div className="flex items-start gap-3 mb-3">
            <div className="w-8 h-8 rounded-lg bg-amber-500/10 flex items-center justify-center shrink-0">
              <KeyRound className="w-4 h-4 text-amber-400" />
            </div>
            <div className="text-xs text-gray-400 leading-relaxed">
              Player passwords are stored as one-way encrypted hashes, so we can't reveal the existing
              one. Instead, set a new temporary password here. The player will be signed out of every
              device immediately and must use the new password to log back in.
            </div>
          </div>
          <div className="grid grid-cols-[1fr_auto_auto] gap-2">
            <Input
              type="text"
              value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
              placeholder="Min 6 characters"
              className="bg-[#0B0E14] border-white/10 font-mono"
              data-testid="reset-password-input"
            />
            <Button size="sm" variant="outline" onClick={generateTempPassword} className="border-white/10 text-xs whitespace-nowrap" data-testid="generate-password-btn">
              <RefreshCw className="w-3 h-3 mr-1" /> Generate
            </Button>
            <Button size="sm" onClick={handleResetPassword} disabled={resetting || !newPassword || newPassword.length < 6}
              className="bg-[#D4AF37] text-black text-xs" data-testid="confirm-reset-password-btn">
              {resetting ? 'Saving…' : 'Set Password'}
            </Button>
          </div>
        </div>
      )}

      {/* Stat Cards */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <StatCard label="Cash Balance" value={fmtMoney(w.cash_balance)} color="#22c55e" icon={Wallet} />
        <StatCard label="FP Credits" value={fmtNumber(w.play_credits_balance || w.play_credits)} color="#3B82F6" icon={Star} />
        <StatCard label="Pending" value={fmtMoney(w.pending)} color="#eab308" icon={Clock} />
        <StatCard label="Game Accounts" value={data.game_accounts?.length || 0} color="#D4AF37" icon={Gamepad2} />
        <StatCard label="Referrals" value={data.referral_count || 0} color="#8b5cf6" icon={Users} />
        <StatCard label="Sessions" value={data.total_sessions || 0} color="#f97316" icon={Activity} />
      </div>

      {/* Adjust Balance */}
      <div className="flex items-center gap-2">
        <Button size="sm" onClick={() => setShowAdjust(!showAdjust)} className="bg-[#D4AF37] text-black text-xs" data-testid="adjust-btn">
          <DollarSign className="w-3 h-3 mr-1" /> Adjust Balance
        </Button>
        {data.game_summary?.load && <span className="text-xs text-gray-500">Loaded: {fmtMoney(data.game_summary.load.total)} ({data.game_summary.load.count} times)</span>}
        {data.game_summary?.redeem && <span className="text-xs text-gray-500 ml-2">Redeemed: {fmtMoney(data.game_summary.redeem.total)} ({data.game_summary.redeem.count} times)</span>}
      </div>
      {showAdjust && (
        <div className="bg-[#131822] rounded-xl border border-white/10 p-4 grid grid-cols-4 gap-3">
          <select value={adjustType} onChange={e => setAdjustType(e.target.value)} className="bg-[#0B0E14] border border-white/10 rounded-lg px-3 py-2 text-sm text-white">
            <option value="add">Add</option>
            <option value="subtract">Subtract</option>
          </select>
          <Input type="number" value={adjustAmount} onChange={e => setAdjustAmount(e.target.value)} placeholder="Amount ($)" className="bg-[#0B0E14] border-white/10" />
          <Input value={adjustReason} onChange={e => setAdjustReason(e.target.value)} placeholder="Reason" className="bg-[#0B0E14] border-white/10" />
          <Button onClick={handleAdjust} disabled={adjusting} className="bg-[#D4AF37] text-black">{adjusting ? 'Processing...' : 'Apply'}</Button>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 bg-[#131822] rounded-lg p-1">
        {tabs.map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)}
            className={`flex-1 py-2 rounded-md text-xs font-medium capitalize transition ${activeTab === tab ? 'bg-[#D4AF37] text-black' : 'text-gray-400 hover:text-white'}`}
            data-testid={`tab-${tab}`}>
            {tab}
          </button>
        ))}
      </div>

      {/* Overview */}
      {activeTab === 'overview' && (
        <div className="bg-[#131822] rounded-2xl border border-white/10 p-5">
          <div className="grid md:grid-cols-2 gap-3">
            <InfoRow label="User ID" value={p.id} copyable />
            <InfoRow label="Username" value={p.username} />
            <InfoRow label="Email" value={p.email} copyable />
            <InfoRow label="Full Name" value={p.full_name || 'Not set'} />
            <InfoRow label="Phone" value={p.phone_number || p.phone_e164 || 'Not set'} />
            <InfoRow label="Referral Code" value={p.referral_code} copyable />
            <InfoRow label="Referred By" value={p.referred_by || p.staff_referral_code || 'None'} />
            <InfoRow label="VIP Tier" value={(p.achievement_tier || 'Bronze').toUpperCase()} />
            <InfoRow label="Total XP" value={fmtNumber(p.total_xp_earned)} />
            <InfoRow label="Referral Count" value={p.referral_count || 0} />
            <InfoRow label="Tenant" value={p.tenant_id || 'Main Platform'} />
            <InfoRow label="Joined" value={p.created_at ? new Date(p.created_at).toLocaleString() : '—'} />
          </div>
          {/* Transaction Summary */}
          {Object.keys(data.transaction_summary || {}).length > 0 && (
            <div className="mt-4 pt-4 border-t border-white/5">
              <h4 className="text-xs text-gray-500 font-medium mb-2">TRANSACTION SUMMARY</h4>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {Object.entries(data.transaction_summary).map(([key, val]) => (
                  <div key={key} className="bg-[#0B0E14] rounded-lg px-3 py-2">
                    <p className="text-[9px] text-gray-500 capitalize">{key.replace(/_/g, ' ')}</p>
                    <p className="text-sm font-semibold text-white">{fmtMoney(val.total)} <span className="text-gray-600">({val.count})</span></p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Games */}
      {activeTab === 'games' && (
        <div className="space-y-2">
          {(data.game_accounts || []).length === 0 ? (
            <div className="text-center py-8 text-gray-500 bg-[#131822] rounded-xl border border-white/10"><Gamepad2 className="w-8 h-8 mx-auto mb-2 opacity-30" /><p className="text-sm">No game accounts</p></div>
          ) : data.game_accounts.map(acc => (
            <div key={acc.id} className="bg-[#131822] rounded-xl border border-white/10 p-4 flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-white">{acc.game_name || acc.game_code}</p>
                <p className="text-[10px] text-gray-500">User: {acc.game_username} | Code: {acc.game_code}</p>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <p className="text-sm font-bold text-[#D4AF37]">{acc.last_known_balance === undefined || acc.last_known_balance === null ? '—' : fmtMoney(acc.last_known_balance)}</p>
                  <p className="text-[9px] text-gray-600">{(acc.last_balance_synced_at || acc.last_synced_at) ? `Synced: ${new Date(acc.last_balance_synced_at || acc.last_synced_at).toLocaleTimeString()}` : 'Not synced'}</p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleAdminCheckBalance(acc)}
                  disabled={!!checkingBalances[acc.id]}
                  className="border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10 text-xs"
                  data-testid={`admin-check-game-balance-${acc.id}`}
                  title="Manual provider balance check"
                >
                  {checkingBalances[acc.id] ? <RefreshCw className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
                  Check Balance
                </Button>
                <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${acc.status === 'active' ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'}`}>{acc.status || 'active'}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Transactions */}
      {activeTab === 'transactions' && (
        <div className="bg-[#131822] rounded-2xl border border-white/10 p-5">
          <h3 className="text-sm font-semibold text-gray-400 mb-3 flex items-center gap-2"><History className="w-4 h-4 text-[#D4AF37]" /> Recent Transactions</h3>
          {(data.recent_transactions || []).length === 0 ? (
            <p className="text-center py-6 text-gray-500 text-sm">No transactions</p>
          ) : (
            <div className="space-y-1.5">
              {data.recent_transactions.map(txn => (
                <div key={txn.id} className="flex items-center justify-between bg-[#0B0E14] rounded-lg px-3 py-2">
                  <div className="flex items-center gap-2.5">
                    <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: txn.type === 'deposit' ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.12)' }}>
                      {txn.type === 'deposit' ? <ArrowDownCircle className="w-3.5 h-3.5 text-green-400" /> : <ArrowUpCircle className="w-3.5 h-3.5 text-red-400" />}
                    </div>
                    <div>
                      <p className="text-xs text-white capitalize">{txn.type}</p>
                      <p className="text-[9px] text-gray-600">{txn.method || ''} {txn.created_at?.slice(0, 16)}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xs font-semibold" style={{ color: txn.type === 'deposit' ? '#22c55e' : '#ef4444' }}>{fmtMoney(txn.amount)}</p>
                    <span className={`text-[8px] px-1.5 py-0.5 rounded-full ${txn.status === 'approved' || txn.status === 'completed' ? 'bg-green-500/20 text-green-400' : txn.status === 'UNDER_REVIEW' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-gray-500/20 text-gray-400'}`}>{txn.status}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Activity */}
      {activeTab === 'activity' && (
        <div className="space-y-4">
          {/* Mini heatmap */}
          {data.activity_heatmap?.max_value > 0 && (
            <div className="bg-[#131822] rounded-2xl border border-white/10 p-5">
              <h3 className="text-sm font-semibold text-gray-400 mb-3">Activity Heatmap (30 days)</h3>
              <div className="min-w-[500px]">
                {DAY_LABELS.map((day, di) => (
                  <div key={di} className="flex items-center mb-0.5">
                    <span className="w-8 text-[8px] text-gray-600 text-right pr-1">{day}</span>
                    <div className="flex flex-1 gap-px">
                      {(data.activity_heatmap.grid?.[di] || Array(24).fill(0)).map((val, hi) => {
                        const max = data.activity_heatmap.max_value || 1;
                        const intensity = val / max;
                        const bg = val === 0 ? 'rgba(255,255,255,0.03)' : `rgba(212,175,55,${0.15 + intensity * 0.85})`;
                        return <div key={hi} className="flex-1 rounded-sm" style={{ background: bg, height: 14 }} title={`${day} ${hi}:00 — ${val} events`} />;
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {/* Recent Activity */}
          <div className="bg-[#131822] rounded-2xl border border-white/10 p-5">
            <h3 className="text-sm font-semibold text-gray-400 mb-3 flex items-center gap-2"><Activity className="w-4 h-4 text-[#D4AF37]" /> Recent Actions</h3>
            {(data.recent_activity || []).length === 0 ? (
              <p className="text-center py-6 text-gray-500 text-sm">No activity recorded</p>
            ) : (
              <div className="space-y-1.5">
                {data.recent_activity.map((act, i) => (
                  <div key={i} className="flex items-center justify-between bg-[#0B0E14] rounded-lg px-3 py-2">
                    <div className="flex items-center gap-2.5">
                      <div className="w-6 h-6 rounded bg-[#D4AF37]/10 flex items-center justify-center">
                        <Activity className="w-3 h-3 text-[#D4AF37]" />
                      </div>
                      <span className="text-xs text-white capitalize">{act.action?.replace('_', ' ')}</span>
                    </div>
                    <span className="text-[10px] text-gray-600">{act.created_at?.slice(0, 16)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Referrals */}
      {activeTab === 'referrals' && (
        <div className="bg-[#131822] rounded-2xl border border-white/10 p-5">
          <h3 className="text-sm font-semibold text-gray-400 mb-3 flex items-center gap-2"><Users className="w-4 h-4 text-[#8b5cf6]" /> Referred Players ({data.referral_count || 0})</h3>
          {(data.referrals || []).length === 0 ? (
            <p className="text-center py-6 text-gray-500 text-sm">No referrals</p>
          ) : (
            <div className="space-y-1.5">
              {data.referrals.map(r => (
                <div key={r.id} className="flex items-center justify-between bg-[#0B0E14] rounded-lg px-3 py-2">
                  <div>
                    <p className="text-sm text-white">{r.username}</p>
                    <p className="text-[10px] text-gray-500">{r.email}</p>
                  </div>
                  <span className="text-[10px] text-gray-600">{r.created_at?.slice(0, 10)}</span>
                </div>
              ))}
            </div>
          )}
          {/* Notifications */}
          {(data.notifications || []).length > 0 && (
            <div className="mt-4 pt-4 border-t border-white/5">
              <h4 className="text-xs text-gray-500 font-medium mb-2 flex items-center gap-1.5"><Bell className="w-3 h-3" /> Recent Notifications</h4>
              <div className="space-y-1">
                {data.notifications.map((n, i) => (
                  <div key={i} className="flex items-center justify-between bg-[#0B0E14] rounded-lg px-3 py-2">
                    <div>
                      <p className="text-xs text-white">{n.title}</p>
                      <p className="text-[10px] text-gray-500">{n.message?.slice(0, 60)}</p>
                    </div>
                    <span className="text-[10px] text-gray-600">{n.created_at?.slice(0, 10)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, color, icon: Icon }) {
  return (
    <div className="bg-[#131822] rounded-xl border border-white/10 p-3">
      <div className="flex items-center gap-2 mb-1"><Icon className="w-3.5 h-3.5" style={{ color }} /><p className="text-[9px] text-gray-500">{label}</p></div>
      <p className="text-base font-bold" style={{ color }}>{value}</p>
    </div>
  );
}

function InfoRow({ label, value, copyable }) {
  const copy = () => { if (value) { navigator.clipboard.writeText(String(value)); toast.success('Copied!'); } };
  return (
    <div className="flex items-center justify-between bg-[#0B0E14] rounded-lg px-3 py-2">
      <span className="text-[10px] text-gray-500">{label}</span>
      <div className="flex items-center gap-1">
        <span className="text-xs text-white font-mono truncate max-w-[200px]">{value}</span>
        {copyable && value && <button onClick={copy} className="text-gray-600 hover:text-gray-400"><Copy className="w-2.5 h-2.5" /></button>}
      </div>
    </div>
  );
}
